import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { enumLabel } from '../../lib/enums';
import { notifyDone, notifyError } from '../../lib/notify';
import { RingsUpAt } from './RingsUpAt';

const ITEMS_PATH = '/admin/v1/catalog/items';
const GROUPS_PATH = '/admin/v1/catalog/modifier-groups';
const LINKS_PATH = '/admin/v1/catalog/item-modifier-groups';
const ALLERGENS_PATH = '/admin/v1/catalog/allergen-flags';

interface ModifierGroupRow {
  id: string;
  nameEn: string;
  required: boolean;
  optionCount: number;
}

interface AttachedQuestion {
  id: string;
  modifierGroupId: string;
  groupName: string;
  required: boolean;
  optionCount: number;
}

interface AllergenRow {
  id: string;
  flag: string;
}

/**
 * Which questions the register asks when this item is rung up.
 *
 * Building a set of choices and putting it on an item were two different things, and only the
 * first had a screen. "Cooked how?" could be written, given five answers and published, and no
 * register ever asked it — because nothing had ever been told which items it belonged to. The mistake
 * was invisible in the office and showed up in the kitchen, as a steak with no temperature on the
 * ticket.
 */
function ItemQuestions({ menuItemId }: { menuItemId: string }) {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);

  const attached = useQuery({
    queryKey: listKey(LINKS_PATH, { menuItemId }),
    queryFn: () => rawApi<AttachedQuestion[]>('GET', `${LINKS_PATH}?menuItemId=${menuItemId}`),
  });
  const groups = useQuery({
    queryKey: listKey(GROUPS_PATH),
    queryFn: () => rawApi<ModifierGroupRow[]>('GET', GROUPS_PATH),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(LINKS_PATH, { menuItemId }) });
    // The list of questions carries how many items ask each one, so it is stale the moment one
    // is attached — otherwise the other screen keeps saying a question is on nothing.
    void queryClient.invalidateQueries({ queryKey: listKey(GROUPS_PATH) });
  };

  const attach = useMutation({
    mutationFn: (modifierGroupId: string) =>
      rawApi('POST', LINKS_PATH, {
        menuItemId,
        modifierGroupId,
        displayOrder: attached.data?.length ?? 0,
      }),
    onSuccess: (_r, id) => {
      setPicked(null);
      refresh();
      const name = groups.data?.find((g) => g.id === id)?.nameEn ?? 'That question';
      notifyDone('Added', `The register will ask "${name}" for this item once you send the menu.`);
    },
    onError: (err) => notifyError(err, 'add this question to the item'),
  });

  const detach = useMutation({
    mutationFn: (linkId: string) => rawApi('DELETE', `${LINKS_PATH}/${linkId}`),
    onSuccess: () => {
      refresh();
      notifyDone('Removed', 'The register will stop asking this for this item.');
    },
    onError: (err) => notifyError(err, 'take this question off the item'),
  });

  const attachedIds = new Set((attached.data ?? []).map((a) => a.modifierGroupId));
  const available = (groups.data ?? []).filter((g) => !attachedIds.has(g.id));

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>What the register asks</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm">
        The questions staff answer when they ring this up — how it is cooked, which rum. Set the questions
        themselves up under Choices and extras, then put them on the items they belong to.
      </Text>

      {attached.isError && (
        <Alert color="red" title="Could not load this">
          The questions on this item could not be reached. Nothing has been lost — this is only a display
          problem.
        </Alert>
      )}

      {attached.isLoading ? (
        <Loader size="sm" />
      ) : (attached.data ?? []).length === 0 ? (
        <Text size="sm" c="dimmed" mb="sm">
          Nothing is asked. Staff ring this up in one tap.
        </Text>
      ) : (
        <Stack gap="xs" mb="sm">
          {(attached.data ?? []).map((q) => (
            <Group key={q.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="wrap">
                <Text size="sm" fw={500}>
                  {q.groupName}
                </Text>
                <Badge size="sm" variant="light" color={q.required ? 'blue' : 'gray'}>
                  {q.required ? 'Must answer' : 'Optional'}
                </Badge>
                {q.optionCount === 0 ? (
                  <Badge size="sm" variant="light" color="orange">
                    No answers yet — nothing will be offered
                  </Badge>
                ) : (
                  <Text size="xs" c="dimmed">
                    {q.optionCount} {q.optionCount === 1 ? 'answer' : 'answers'}
                  </Text>
                )}
              </Group>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={() => detach.mutate(q.id)}
                loading={detach.isPending}
              >
                Remove
              </Button>
            </Group>
          ))}
        </Stack>
      )}

      <Group gap="xs" align="flex-end">
        <Select
          style={{ minWidth: 260, flex: 1 }}
          label="Ask something else"
          placeholder={
            groups.isLoading
              ? 'Loading…'
              : available.length === 0
                ? 'Every question you have set up is already on this item'
                : 'Choose a question'
          }
          data={available.map((g) => ({
            value: g.id,
            label: g.optionCount === 0 ? `${g.nameEn} (no answers yet)` : g.nameEn,
          }))}
          value={picked}
          onChange={setPicked}
          disabled={groups.isLoading || available.length === 0}
          searchable={available.length > 8}
          comboboxProps={{ withinPortal: true }}
        />
        <Button disabled={!picked} loading={attach.isPending} onClick={() => picked && attach.mutate(picked)}>
          Add
        </Button>
      </Group>
    </Paper>
  );
}

/** Things a guest asks about at the table. The first seven are what it contains. */
const CONTAINS = ['gluten', 'dairy', 'nuts', 'shellfish', 'fish', 'egg', 'soy'];
/** The rest are what it suits, plus heat — asked just as often, and not an allergy. */
const GOOD_TO_KNOW = ['vegan', 'vegetarian', 'spicy'];

/**
 * What is in a dish, for the question staff are asked at the table every single night.
 *
 * Allergens could be recorded and never read: there was a way to flag one and no way to list them,
 * anywhere, so none were ever flagged and "does the ceviche have shellfish?" was answered from
 * whoever's memory was nearest. A checkbox is the whole interaction — the kitchen knows the answer,
 * it just had nowhere to put it.
 */
function ItemAllergens({ menuItemId }: { menuItemId: string }) {
  const queryClient = useQueryClient();
  const query = { targetType: 'menuItem', targetId: menuItemId };

  const flags = useQuery({
    queryKey: listKey(ALLERGENS_PATH, query),
    queryFn: () =>
      rawApi<AllergenRow[]>('GET', `${ALLERGENS_PATH}?targetType=menuItem&targetId=${menuItemId}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey(ALLERGENS_PATH, query) });

  const toggle = useMutation({
    mutationFn: ({ flag, existingId }: { flag: string; existingId?: string }) =>
      existingId
        ? rawApi('DELETE', `${ALLERGENS_PATH}/${existingId}`)
        : rawApi('POST', ALLERGENS_PATH, { targetType: 'menuItem', targetId: menuItemId, flag }),
    onSuccess: () => refresh(),
    onError: (err) => notifyError(err, 'change what this dish is marked as'),
  });

  const idFor = (flag: string) => (flags.data ?? []).find((f) => f.flag === flag)?.id;

  const row = (values: string[]) => (
    <Group gap="lg" wrap="wrap">
      {values.map((flag) => (
        <Checkbox
          key={flag}
          label={enumLabel('allergenFlag', flag)}
          checked={Boolean(idFor(flag))}
          disabled={flags.isLoading || toggle.isPending}
          onChange={() => toggle.mutate({ flag, existingId: idFor(flag) })}
        />
      ))}
    </Group>
  );

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>Allergens</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm">
        What staff answer when a guest asks. Saved as you tick — no need to send the menu for this.
      </Text>
      {flags.isError ? (
        <Alert color="red" title="Could not load this">
          What this dish is marked as could not be reached. Nothing has been lost — this is only a display
          problem.
        </Alert>
      ) : (
        <Stack gap="sm">
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              Contains
            </Text>
            {row(CONTAINS)}
          </div>
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              Good to know
            </Text>
            {row(GOOD_TO_KNOW)}
          </div>
        </Stack>
      )}
    </Paper>
  );
}

/**
 * One item, opened.
 *
 * Three things about a dish live in tables of their own and had no screen at all: the sizes it is
 * sold in, the questions the register asks about it, and what it contains. All three are the answer to
 * "set this item up properly", and none of them are a column — so they are here, behind the item
 * itself, rather than as three more lists a manager would have to know existed.
 */
export function ItemSetupModal({
  item,
  onClose,
}: {
  item: Record<string, unknown> | null;
  onClose: () => void;
}) {
  const id = item ? String(item.id) : '';
  const name = item ? String(item.nameEn ?? 'This item') : '';

  return (
    <Modal
      opened={Boolean(item)}
      onClose={onClose}
      title={name}
      size="xl"
      // The price editor on a size row opens in a layer of its own, which counts as "outside" this
      // dialog — so typing a price closed the whole item and threw the half-typed price away.
      // Half-finished work here is lost to a stray click for the same reason everywhere else.
      closeOnClickOutside={false}
    >
      {item && (
        <>
          <CrudSection
            title="Sizes"
            entityNoun="size"
            description="What this is sold as — bottle, draft, a pitcher. Each size is priced on its own: tap what it rings up at to set or change the price. Every item starts with one size named after itself; rename it if you sell more than one."
            listPath={`${ITEMS_PATH}/${id}/variants`}
            createPath="/admin/v1/catalog/variants"
            updatePathTemplate="/admin/v1/catalog/variants/{id}"
            defaults={{ menuItemId: id }}
            rowLabel={(r) => String(r.nameEn ?? 'this size')}
            emptyMessage="No sizes yet."
            columns={[
              { accessor: 'nameEn', title: 'Size' },
              // The one thing a manager opens this to check, and — since a size is priced on its
              // own — the one thing that stops a new "Pitcher" from being rung up at all. It is
              // editable here because this row is already the size the price is about; the Prices
              // screen would ask which list and then which size, having been told both.
              {
                accessor: 'id',
                title: 'Rings up at',
                render: (r) => {
                  // Every item starts with one size named after itself, so "Conch Fritters —
                  // Conch Fritters" is the common case, not the odd one.
                  const size = String(r.nameEn ?? '');
                  return (
                    <RingsUpAt
                      targetType="variant"
                      targetId={String(r.id)}
                      name={!size || size === name ? name : `${name} — ${size}`}
                    />
                  );
                },
              },
              {
                accessor: 'isDefault',
                title: 'Offered first',
                render: (r) => (r.isDefault ? <Badge variant="light">Offered first</Badge> : null),
              },
              {
                accessor: 'active',
                title: 'On the register',
                render: (r) => (
                  <Badge color={r.active ? 'teal' : 'gray'} variant="light">
                    {r.active ? 'Selling' : 'Off'}
                  </Badge>
                ),
              },
            ]}
            fields={[
              {
                name: 'nameEn',
                label: 'Size',
                type: 'text',
                required: true,
                description: 'What staff read on the register, such as "Pitcher"',
              },
              { name: 'nameEs', label: 'Size in Spanish', type: 'text', nullable: true },
              {
                name: 'isDefault',
                label: 'Offer this one first',
                type: 'boolean',
                description: 'The size staff get without choosing — set it on the one you sell most of',
              },
              {
                name: 'displayOrder',
                label: 'Position in the list',
                type: 'number',
                defaultValue: 0,
                description: 'Lower numbers come first',
              },
              {
                name: 'active',
                label: 'Sell this size',
                type: 'boolean',
                defaultValue: true,
                description: 'Turn it off to stop selling it while keeping what it has already sold',
              },
            ]}
          />

          <ItemQuestions menuItemId={id} />
          <ItemAllergens menuItemId={id} />
        </>
      )}
    </Modal>
  );
}
